/**
 * DesignerBoqTab - the BOQ tab of the designer project detail page, in the
 * Studio theme. A summary header (current value, status counts, last
 * updated) sits above one card per BOQ.
 */

import { toProjectParticipantRole } from "@shared/types";
import { ArrowRight, Plus, Sparkles } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { ShareAction } from "@/features/sharing/components/share-action";
import {
	Eyebrow,
	FloatPanel,
	ItalicAccent,
	Pill,
	type PillTone,
	StudioButton,
} from "@/features/studio/theme";
import { useProjectBOQs } from "../../hooks/use-boq";
import {
	type BOQ,
	type BoqStatus,
	formatCurrency,
} from "../../services/boq/boq-api.service";

const STATUS_ORDER: readonly BoqStatus[] = [
	"draft",
	"sent",
	"change_requested",
	"approved",
	"locked",
];

const STATUS_TONE: Record<BoqStatus, PillTone> = {
	draft: "neutral",
	sent: "info",
	change_requested: "awaiting",
	approved: "done",
	locked: "active",
};

interface BoqSummary {
	currentValue: number;
	currencyCode: string;
	statusCounts: Array<{ status: BoqStatus; count: number }>;
	lastUpdatedAt: string | null;
}

/**
 * Each new BOQ a user creates points at their previous one through
 * `previousBoqId`, so the list holds every version. The current value sums
 * only the BOQs that no other BOQ in the list supersedes.
 */
function summarize(boqs: BOQ[]): BoqSummary {
	const superseded = new Set(
		boqs.map((boq) => boq.previousBoqId).filter(Boolean),
	);
	const current = boqs.filter((boq) => !superseded.has(boq.id));
	const currentValue = current.reduce(
		(sum, boq) => sum + (boq.grandTotal ?? 0),
		0,
	);

	const statusCounts = STATUS_ORDER.map((status) => ({
		status,
		count: boqs.filter((boq) => boq.status === status).length,
	})).filter((entry) => entry.count > 0);

	const lastUpdatedAt = boqs.reduce<string | null>((latest, boq) => {
		if (!latest) return boq.updatedAt;
		return new Date(boq.updatedAt) > new Date(latest) ? boq.updatedAt : latest;
	}, null);

	return {
		currentValue,
		currencyCode: current[0]?.currencyCode ?? "INR",
		statusCounts,
		lastUpdatedAt,
	};
}

function formatDate(value: string): string {
	return new Date(value).toLocaleDateString();
}

interface DesignerBoqTabProps {
	projectId: string;
}

export function DesignerBoqTab({
	projectId,
}: DesignerBoqTabProps): React.ReactElement {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const userType = useAuthStore((state) => state.getUserType());
	const projectRole = toProjectParticipantRole(userType);
	const canAuthor = projectRole !== null && projectRole !== "homeowner";

	// The detail page changes BOQ totals and statuses (entries, send, lock)
	// without touching this query's cache, so the list is refetched on every
	// visit to the tab rather than served from cache.
	const {
		data: boqs,
		isLoading,
		isError,
		error,
		isFetching,
		refetch,
	} = useProjectBOQs(projectId, { refetchOnMount: "always" });

	const summary = useMemo(() => summarize(boqs ?? []), [boqs]);

	const goToNewBoq = (): void => {
		navigate(`/projects/${projectId}/boqs/new`);
	};
	const goToStarterBoq = (): void => {
		navigate(`/projects/${projectId}/boqs/new?mode=starter`);
	};

	const createActions = canAuthor ? (
		<div className="flex flex-wrap gap-2">
			<StudioButton
				variant="secondary"
				size="md"
				icon={Sparkles}
				onClick={goToStarterBoq}
			>
				{t("boq.designerTab.fromTemplate")}
			</StudioButton>
			<StudioButton
				variant="primary"
				size="md"
				icon={Plus}
				onClick={goToNewBoq}
			>
				{t("boq.designerTab.newBoq")}
			</StudioButton>
		</div>
	) : null;

	const header = (
		<div className="flex flex-wrap items-end justify-between gap-4">
			<div>
				<Eyebrow style={{ fontSize: 9 }}>
					{t("boq.designerTab.eyebrow")}
				</Eyebrow>
				<h2
					className="text-dashboard-text"
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 500,
						fontSize: 34,
						lineHeight: 1.05,
						letterSpacing: "-0.02em",
						margin: "8px 0 0",
					}}
				>
					{t("boq.designerTab.titleLead")}{" "}
					<ItalicAccent
						style={{
							fontSize: 34,
							color: "var(--color-dashboard-accent-display)",
						}}
					>
						{t("boq.designerTab.titleAccent")}
					</ItalicAccent>
				</h2>
			</div>
			{boqs && boqs.length > 0 ? createActions : null}
		</div>
	);

	if (isLoading) {
		return (
			<div className="space-y-6">
				{header}
				<Skeleton
					data-testid="designer-boq-skeleton"
					className="h-28 w-full rounded-2xl bg-dashboard-surface"
				/>
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
					{[0, 1, 2].map((slot) => (
						<Skeleton
							key={slot}
							data-testid="designer-boq-skeleton"
							className="h-44 rounded-2xl bg-dashboard-surface"
						/>
					))}
				</div>
			</div>
		);
	}

	if (isError) {
		// A contractor's BOQ access ends with the assignment, so a retry cannot
		// succeed and would only repeat the refusal.
		if ((error as { forbidden?: boolean } | null)?.forbidden) {
			return (
				<div className="space-y-6">
					{header}
					<FloatPanel elevation={1} padding={24}>
						<p className="text-center text-sm text-dashboard-muted">
							{t("boq.designerTab.accessEnded")}
						</p>
					</FloatPanel>
				</div>
			);
		}
		return (
			<div className="space-y-6">
				{header}
				<FloatPanel elevation={1} padding={24}>
					<QueryErrorState
						message={t("boq.designerTab.loadError")}
						onRetry={() => void refetch()}
						isRetrying={isFetching}
					/>
				</FloatPanel>
			</div>
		);
	}

	if (!boqs || boqs.length === 0) {
		return (
			<div className="space-y-6">
				{header}
				<FloatPanel elevation={1} padding={32}>
					<div className="flex flex-col items-center gap-3 text-center">
						<p
							className="text-dashboard-text"
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontSize: 20,
							}}
						>
							{t("boq.noBoqsYet")}
						</p>
						<p className="max-w-sm text-sm text-dashboard-muted">
							{t("boq.noBoqsDesc")}
						</p>
						{createActions && <div className="mt-3">{createActions}</div>}
					</div>
				</FloatPanel>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{header}

			<FloatPanel elevation={2} padding={24}>
				<div
					data-testid="designer-boq-summary"
					className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between"
				>
					<div>
						<Eyebrow style={{ fontSize: 9 }}>
							{t("boq.designerTab.currentValue")}
						</Eyebrow>
						<p
							className="text-dashboard-text"
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 30,
								letterSpacing: "-0.02em",
								margin: "6px 0 2px",
							}}
						>
							{formatCurrency(summary.currentValue, summary.currencyCode)}
						</p>
						<p className="text-xs text-dashboard-muted">
							{t("boq.designerTab.currentValueHint")}
						</p>
					</div>
					<div className="flex flex-col gap-2 md:items-end">
						<div className="flex flex-wrap gap-2">
							<Pill tone="neutral">
								{t("boq.designerTab.boqCount", { count: boqs.length })}
							</Pill>
							{summary.statusCounts.map(({ status, count }) => (
								<Pill key={status} tone={STATUS_TONE[status]} dot>
									{t("boq.designerTab.statusCount", {
										count,
										status: t(`boq.status.${status}`),
									})}
								</Pill>
							))}
						</div>
						{summary.lastUpdatedAt && (
							<p className="text-xs text-dashboard-muted">
								{t("boq.designerTab.lastUpdated", {
									date: formatDate(summary.lastUpdatedAt),
								})}
							</p>
						)}
					</div>
				</div>
			</FloatPanel>

			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
				{boqs.map((boq) => (
					<FloatPanel key={boq.id} elevation={1} padding={0}>
						<div data-testid={`designer-boq-card-${boq.id}`}>
							<div className="border-b border-dashboard-border px-5 py-4">
								<div className="mb-2 flex items-start justify-between gap-3">
									<Eyebrow style={{ fontSize: 9 }}>
										{t("boq.version", { version: boq.versionNumber })}
									</Eyebrow>
									<Pill tone={STATUS_TONE[boq.status]} dot>
										{t(`boq.status.${boq.status}`)}
									</Pill>
								</div>
								<p
									className="truncate text-dashboard-text"
									style={{
										fontFamily: "var(--font-display)",
										fontWeight: 500,
										fontSize: 18,
									}}
								>
									{boq.title ||
										t("boq.version", { version: boq.versionNumber })}
								</p>
								<div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-dashboard-muted">
									<span>{t("boq.createdBy", { role: boq.createdByRole })}</span>
									{boq.generationRun && (
										<Pill tone="active" style={{ height: 20, fontSize: 11 }}>
											{t("boq.starter.label")}
										</Pill>
									)}
								</div>
							</div>
							<div className="px-5 py-4">
								<div className="flex items-baseline justify-between">
									<span className="text-xs text-dashboard-muted">
										{t("boq.totalAmount")}
									</span>
									<span className="text-base font-semibold text-dashboard-text">
										{formatCurrency(boq.grandTotal ?? 0, boq.currencyCode)}
									</span>
								</div>
								<p className="mt-1 text-xs text-dashboard-muted">
									{t("boq.designerTab.updatedAt", {
										date: formatDate(boq.updatedAt),
									})}
								</p>
								<div className="mt-4 flex flex-wrap gap-2">
									<StudioButton
										variant="secondary"
										size="sm"
										iconRight={ArrowRight}
										onClick={() => navigate(`/boqs/${boq.id}`)}
									>
										{t("boq.designerTab.open")}
									</StudioButton>
									{canAuthor && (
										<ShareAction
											artifact={{
												type: "boq",
												id: boq.id,
												projectId: boq.projectId,
											}}
											label={t("boq.designerTab.share")}
										/>
									)}
								</div>
							</div>
						</div>
					</FloatPanel>
				))}
			</div>
		</div>
	);
}
